<template>
  <div class="vdm">
    <div
      v-if="loading"
      class="vdm-empty"
    >
      <feather-icon
        icon="LoaderIcon"
        size="32"
        class="vdm-spinner"
      />
      <p>{{ $t('Loading') }}...</p>
    </div>

    <div
      v-else-if="!devices.length"
      class="vdm-empty"
    >
      <feather-icon
        icon="CpuIcon"
        size="32"
      />
      <p>{{ $t('No devices linked to this vehicle') }}</p>
    </div>

    <div
      v-else
      class="vdm-body"
    >
      <div class="vdm-side">
        <div class="vdm-side-head">
          <span class="vdm-count">
            {{ shownCount }} {{ $t('of') }} {{ mappableCount }} {{ $t('on the map') }}
          </span>
          <button
            type="button"
            class="vdm-link"
            :disabled="!mappableCount"
            @click="toggleAll"
          >
            {{ allShown ? $t('Hide all devices') : $t('Show all devices') }}
          </button>
        </div>

        <div class="vdm-list">
          <div
            v-for="device in devices"
            :key="device.id"
            class="vdm-item"
            :class="{ 'is-off': !visible[device.id], 'is-unmappable': !hasPosition(device) }"
          >
            <label class="vdm-check">
              <input
                type="checkbox"
                :checked="!!visible[device.id]"
                :disabled="!hasPosition(device)"
                @change="toggle(device)"
              >
            </label>

            <div class="vdm-item-body">
              <div class="vdm-item-name">
                {{ device.label }}
                <span
                  v-if="device.is_primary"
                  class="vdm-badge"
                >{{ $t('Main') }}</span>
              </div>
              <div class="vdm-item-meta">
                {{ (device.device_type && device.device_type.name) || $t('Uncategorized') }}
                <template v-if="device.position">
                  &middot; {{ formatDate(device.position.last_tracking_at) }}
                </template>
                <template v-else>
                  &middot; {{ $t('No position') }}
                </template>
              </div>
              <div
                v-if="distanceFromPrimary(device)"
                class="vdm-item-distance"
              >
                {{ distanceFromPrimary(device) }} {{ $t('from the main device') }}
              </div>
            </div>

            <button
              v-if="hasPosition(device)"
              type="button"
              class="vdm-zoom"
              :title="$t('Zoom to device')"
              @click="focus(device)"
            >
              <i class="fa fa-search-plus" />
            </button>

            <button
              type="button"
              class="vdm-zoom"
              :title="$t('See device')"
              @click="abrirDispositivo(device)"
            >
              <i class="fa fa-external-link-alt" />
            </button>
          </div>
        </div>
      </div>

      <div class="vdm-map">
        <div class="vdm-map-actions">
          <button
            type="button"
            class="vdm-fit"
            @click="fitAll"
          >
            <i class="fa fa-compress" /> {{ $t('Frame all') }}
          </button>
        </div>

        <GmapMap
          ref="mapRef"
          :center="center"
          :zoom="zoom"
          map-type-id="roadmap"
          style="width: 100%; height: 460px"
          :options="mapOptions"
        >
          <GmapMarker
            v-for="device in positioned"
            :key="device.id"
            :position="positionOf(device)"
            :icon="iconOf(device)"
            :label="labelOf(device)"
            @click="focus(device)"
          />
        </GmapMap>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { gmapApi } from 'vue2-google-maps'

const GENERIC_PIN = require('@/assets/images/pin/map-pin-generic.svg')

export default {
  name: 'VehicleDevicesMap',

  props: {
    vehicleId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      devices: [],
      visible: {},
      center: { lat: -8.05, lng: -34.9 },
      zoom: 13,
      mapOptions: {
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      },
    }
  },

  computed: {
    google: gmapApi,

    mappable() {
      return this.devices.filter(device => this.hasPosition(device))
    },

    mappableCount() {
      return this.mappable.length
    },

    positioned() {
      return this.mappable.filter(device => this.visible[device.id])
    },

    shownCount() {
      return this.positioned.length
    },

    allShown() {
      return this.mappableCount > 0 && this.shownCount === this.mappableCount
    },

    primary() {
      return this.mappable.find(device => device.is_primary) || null
    },
  },

  watch: {
    vehicleId(novo, antigo) {
      if (novo !== antigo) {
        this.unsubscribe(antigo)
        this.load()
        this.subscribe()
      }
    },
  },

  created() {
    this.load()
  },

  mounted() {
    this.subscribe()
  },

  beforeDestroy() {
    this.unsubscribe()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const response = await axios.get(
          `${this.$store.state.api}vehicles/${this.vehicleId}/devices/positions`,
        )
        this.devices = (response.data && response.data.data) || []
        this.visible = this.devices.reduce((acc, device) => {
          acc[device.id] = this.hasPosition(device)
          return acc
        }, {})
        this.$nextTick(this.fitAll)
      } catch (error) {
        this.$error(error)
      } finally {
        this.loading = false
      }
    },

    channelName(vehicleId) {
      return `device-last-update.${vehicleId || this.vehicleId}`
    },

    subscribe() {
      if (!window.Echo || !window.Echo.channel) {
        return
      }

      window.Echo.channel(this.channelName())
        .listen('.device-last-update', this.onDevicePosition)
    },

    unsubscribe(vehicleId) {
      if (!window.Echo || !window.Echo.channel) {
        return
      }

      const channel = this.channelName(vehicleId)

      try {
        window.Echo.channel(channel).stopListening('.device-last-update')
        window.Echo.leave(channel)
      } catch (e) {
        window.console.warn('VehicleDevicesMap: falha ao encerrar o canal', e)
      }
    },

    onDevicePosition(payload) {
      const index = this.devices.findIndex(device => device.id === payload.device_id)

      if (index === -1) {
        this.load()
        return
      }

      const atualizado = {
        ...this.devices[index],
        is_primary: payload.is_primary,
        position: {
          lat: payload.lat,
          long: payload.long,
          speed: payload.speed,
          device_battery_level: payload.device_battery_level,
          last_tracking_at: payload.last_tracking_at,
        },
      }

      this.devices.splice(index, 1, atualizado)

      if (this.visible[atualizado.id] === undefined) {
        this.$set(this.visible, atualizado.id, true)
      }
    },

    hasPosition(device) {
      return !!(device.position && device.position.lat && device.position.long)
    },

    positionOf(device) {
      return {
        lat: parseFloat(device.position.lat),
        lng: parseFloat(device.position.long),
      }
    },

    iconOf(device) {
      if (!this.google) {
        return null
      }

      const url = (device.device_type && device.device_type.file) || GENERIC_PIN

      return {
        url,
        scaledSize: new this.google.maps.Size(40, 60),
        anchor: new this.google.maps.Point(20, 60),
        origin: new this.google.maps.Point(0, 0),
        labelOrigin: new this.google.maps.Point(20, 74),
      }
    },

    labelOf(device) {
      return {
        text: device.label,
        className: 'map-pin-label',
        color: '#ffffff',
        fontSize: '11px',
        fontWeight: '600',
      }
    },

    abrirDispositivo(device) {
      this.$router.push(`/devices/view/${device.id}`)
    },

    toggle(device) {
      if (!this.hasPosition(device)) {
        return
      }

      this.$set(this.visible, device.id, !this.visible[device.id])
    },

    toggleAll() {
      const mostrar = !this.allShown

      this.mappable.forEach(device => {
        this.$set(this.visible, device.id, mostrar)
      })

      if (mostrar) {
        this.$nextTick(this.fitAll)
      }
    },

    focus(device) {
      if (!this.hasPosition(device)) {
        return
      }

      this.$set(this.visible, device.id, true)
      this.center = this.positionOf(device)
      this.zoom = 17
    },

    fitAll() {
      const map = this.$refs.mapRef

      if (!map || !this.google || !this.positioned.length) {
        return
      }

      const bounds = new this.google.maps.LatLngBounds()
      this.positioned.forEach(device => bounds.extend(this.positionOf(device)))

      map.$mapPromise.then(instancia => instancia.fitBounds(bounds))
    },

    distanceFromPrimary(device) {
      if (!this.primary || device.id === this.primary.id || !this.hasPosition(device)) {
        return null
      }

      const metros = this.haversine(this.positionOf(this.primary), this.positionOf(device))

      if (metros < 100) {
        return null
      }

      return metros < 1000
        ? `${Math.round(metros)} m`
        : `${(metros / 1000).toFixed(1)} km`
    },

    haversine(a, b) {
      const R = 6371000
      const rad = valor => (valor * Math.PI) / 180
      const dLat = rad(b.lat - a.lat)
      const dLng = rad(b.lng - a.lng)

      const h = Math.sin(dLat / 2) ** 2
        + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2

      return 2 * R * Math.asin(Math.sqrt(h))
    },

    formatDate(valor) {
      if (!valor) {
        return '-'
      }

      return new Date(String(valor).replace(' ', 'T')).toLocaleString('pt-BR')
    },
  },
}
</script>

<style scoped>
.vdm-body {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.vdm-side {
  flex: 0 0 290px;
  display: flex;
  flex-direction: column;
  max-height: 500px;
}

.vdm-side-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.15rem 0.5rem;
  font-size: 0.75rem;
  color: #6e6b7b;
}

.vdm-link {
  border: 0;
  background: none;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #7367f0;
}

.vdm-link:disabled {
  color: #b9b9c3;
}

.vdm-list {
  overflow-y: auto;
}

.vdm-map {
  flex: 1 1 auto;
  min-width: 0;
}

.vdm-map-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.vdm-fit {
  border: 1px solid #d8d6de;
  background: #fff;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.8rem;
  color: #5e5873;
}

.vdm-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fff;
  padding: 0.6rem 0.5rem;
  margin-bottom: 0.5rem;
}

.vdm-item.is-off {
  background: #f8f8f8;
}

.vdm-item.is-off .vdm-item-name,
.vdm-item.is-off .vdm-item-meta {
  color: #b9b9c3;
}

.vdm-check {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  align-self: stretch;
  margin: 0;
  cursor: pointer;
}

.vdm-check input {
  width: 17px;
  height: 17px;
  cursor: pointer;
}

.vdm-item.is-unmappable .vdm-check,
.vdm-item.is-unmappable .vdm-check input {
  cursor: not-allowed;
}

.vdm-item-body {
  flex: 1 1 auto;
  min-width: 0;
}

.vdm-item-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: #5e5873;
}

.vdm-item-meta,
.vdm-item-distance {
  font-size: 0.75rem;
  color: #6e6b7b;
}

.vdm-item-distance {
  color: #ea5455;
  font-weight: 600;
}

.vdm-badge {
  background: #7367f0;
  color: #fff;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.65rem;
  margin-left: 4px;
  vertical-align: middle;
}

.vdm-zoom {
  flex: 0 0 30px;
  align-self: center;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  background: #fff;
  padding: 4px 0;
  color: #7367f0;
}

.vdm-zoom:hover {
  background: #f3f2ff;
}

.vdm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: #6e6b7b;
  text-align: center;
}

.vdm-spinner {
  animation: vdm-spin 1s linear infinite;
}

@keyframes vdm-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
